// Tenantra Inc. - Back office: processing fees tenants pay for each payment method
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import api, { apiHelpers } from '@/services/api';

const ORDER = ['bank', 'interac', 'debit_card', 'credit_card'];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function PaymentFeesPanel() {
  const [fees, setFees] = useState(null);
  const [busy, setBusy] = useState(false);
  const [example, setExample] = useState(2000);
  useEffect(() => { api.get('/admin/payment-fees').then(r => setFees(r.data.fees)); }, []);

  const set = (k, x) => (e) => setFees({ ...fees, [k]: { ...fees[k], [x]: e.target.value.replace(/[^\d.]/g, '') } });
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(ORDER.map(k => [k, { pct: Number(fees[k].pct) || 0, flat: Number(fees[k].flat) || 0 }]));
      const r = await api.put('/admin/payment-fees', body); setFees(r.data.fees); toast.success('Fees saved. Tenants see them on their next payment.');
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };
  if (!fees) return <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="payment-fees-panel">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Payment Fees</h1>
        <p className="text-sm text-slate-500">The processing fee a tenant pays for each way to pay. A fee can be a percentage, a flat amount, or both. Applies to auto-pay and one-time payments.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
            <th className="font-medium px-4 py-2.5">Method</th><th className="font-medium px-3 py-2.5">Percentage</th><th className="font-medium px-3 py-2.5">Flat fee</th>
            <th className="font-medium px-4 py-2.5 text-right">On {money(example)} rent</th></tr></thead>
          <tbody>
            {ORDER.map(k => {
              const f = fees[k]; const fee = example * (Number(f.pct) || 0) / 100 + (Number(f.flat) || 0);
              return (
                <tr key={k} className="border-b border-slate-50 last:border-0" data-testid={`fee-row-${k}`}>
                  <td className="px-4 py-3"><p className="font-medium text-slate-800">{f.label}</p><p className="text-[11px] text-slate-400">{f.timing}</p></td>
                  <td className="px-3 py-3"><div className="flex items-center gap-1"><Input value={f.pct} onChange={set(k, 'pct')} inputMode="decimal" className="h-9 w-20 text-sm" data-testid={`fee-${k}-pct`} /><span className="text-slate-400">%</span></div></td>
                  <td className="px-3 py-3"><div className="flex items-center gap-1"><span className="text-slate-400">$</span><Input value={f.flat} onChange={set(k, 'flat')} inputMode="decimal" className="h-9 w-20 text-sm" data-testid={`fee-${k}-flat`} /></div></td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-800" data-testid={`fee-${k}-example`}>{fee > 0 ? money(fee) : 'No fee'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-xs text-slate-500 flex items-center gap-2">Example rent
          <Input value={example} onChange={(e) => setExample(Number(e.target.value.replace(/\D/g, '')) || 0)} className="h-8 w-24 text-sm" data-testid="fee-example-rent" /></label>
        <button onClick={save} disabled={busy} className="h-9 px-5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2" data-testid="fee-save-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save fees</button>
      </div>
      <p className="text-xs text-slate-400">Card network rules still apply to credit card fees (a maximum surcharge). Credit card fees are set to $0 automatically for tenants whose rental is in Quebec.</p>
    </div>
  );
}
